import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'ghost' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  isLoading = false,
  leftIcon,
  rightIcon,
  className,
  disabled,
  ...props
}: ButtonProps) {
  return (
    <button
      className={cn(
        'font-semibold transition-colors transition-shadow duration-200 inline-flex items-center justify-center whitespace-nowrap relative overflow-hidden group active:scale-[0.98]',
        variant === 'primary' && [
          'bg-gradient-to-r from-indigo-600 via-indigo-500 to-purple-500',
          'hover:from-indigo-500 hover:via-indigo-400 hover:to-purple-400',
          'text-white',
          'shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/30',
          'border border-white/10',
        ],
        variant === 'secondary' && [
          'bg-slate-800 hover:bg-slate-700',
          'text-white border border-slate-700 hover:border-slate-600',
        ],
        variant === 'ghost' && [
          'bg-transparent hover:bg-slate-800/50',
          'text-slate-400 hover:text-white',
        ],
        variant === 'outline' && [
          'bg-transparent border border-indigo-500/40 hover:border-indigo-400/70',
          'text-indigo-400 hover:text-indigo-300',
          'hover:bg-indigo-500/10',
          'hover:shadow-lg hover:shadow-indigo-500/10',
        ],
        size === 'sm' && 'px-3 py-1.5 text-xs rounded-lg gap-1.5',
        size === 'md' && 'px-4 py-2.5 text-sm rounded-lg gap-2',
        size === 'lg' && 'px-8 py-3.5 text-base rounded-xl gap-2.5',
        (disabled || isLoading) && 'opacity-60 cursor-not-allowed',
        className
      )}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : leftIcon}
      {children}
      {!isLoading && rightIcon}
    </button>
  );
}
